import { ClipboardIcon } from "@storytell/bsky-icons";
import type { Block, StreamingBlock } from "@storytell/streaming";
import { BlockRenderer } from "@storytell/streaming";
import { Show, createSignal, onCleanup, onMount } from "solid-js";
import { useAppUI } from "~/components/layouts/minimal/AppUIContext";
import { BlockHeaderPill } from "./internal/BlockHeaderPill";
import { copyBlockDebugInfoToClipboard } from "./internal/blockDebugUtils";
import { getBlockIconComponent } from "./internal/blockIcons";

interface InterstitialDataRendererProps {
  block: StreamingBlock;
  onBlockClick?: () => void;
}

/**
 * Renders data-oriented interstitial blocks with a pill header showing title and processing state.
 * Shows egress credits instead of content. Debug mode enables clipboard functionality.
 */
export const InterstitialDataRenderer = (props: InterstitialDataRendererProps) => {
  const { state } = useAppUI();
  const [currentContent, setCurrentContent] = createSignal("");

  const title = () => props.block.title || props.block.activity || "Data";
  const egressCredits = () => props.block.metrics?.egressCredits || 0;

  const formatCredits = (credits: number): string => {
    if (credits === 0) return "0 credits";
    return `${credits.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} credits`;
  };

  const handleCopyDebugInfo = async (e: MouseEvent) => {
    e.stopPropagation();
    await copyBlockDebugInfoToClipboard(props.block);
  };

  const handleCopyContent = async (e: MouseEvent) => {
    e.stopPropagation();
    const content = currentContent();
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
      } catch (error) {
        // Silently handle clipboard errors
      }
    }
  };

  return (
    <Show when={!props.block.isComplete || egressCredits() > 0}>
      <div class="group" data-renderer="interstitial-data-renderer">
        <div class="flex items-center gap-2">
          <Show when={!props.block.isComplete}>
            <BlockHeaderPill
              title={title()}
              icon={getBlockIconComponent(props.block, BlockRenderer.INTERSTITIAL_DATA, { variant: "purple" })}
              isProcessing={!props.block.isComplete}
              onClick={props.onBlockClick || (() => {})}
              onCopyDebugInfo={handleCopyDebugInfo}
              variant="purple"
            />
          </Show>

          <Show when={state.isDebugMode && currentContent()}>
            <button
              type="button"
              onClick={handleCopyContent}
              class="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Copy content to clipboard"
            >
              <ClipboardIcon size={12} class="text-gray-500 dark:text-gray-400" />
            </button>
          </Show>
        </div>

        <Show when={egressCredits() > 0}>
          <div
            class="ml-6 text-xs text-gray-500 dark:text-gray-400"
            classList={{
              "mt-3": !props.block.isComplete,
            }}
          >
            {formatCredits(egressCredits())}
          </div>
        </Show>
      </div>
    </Show>
  );
};
